import type { DateTime } from 'luxon';
import type {
  TimelineItemPositioningProps,
  TimelineOrientation,
  TimelineScale,
} from './Timeline.types';

/**
 * Calculate Positioning for `Timeline.Lane` items like `Event` and `Memory`
 */
export const timelineItemPositioning = (props: TimelineItemPositioningProps) => {
  const { orientation, offsetPx, sizePx } = props;

  const isRelative = offsetPx === undefined;

  return {
    position: !isRelative ? ('absolute' as const) : undefined,
    top: orientation === 'vertical' ? offsetPx : 0,
    right: orientation === 'vertical' ? 0 : undefined,
    bottom: orientation === 'horizontal' ? 0 : undefined,
    left: orientation === 'horizontal' ? offsetPx : 0,
    height: orientation === 'vertical' ? sizePx : undefined,
    width: orientation === 'horizontal' ? sizePx : undefined,
  };
};

type ScaleSelectionParams = {
  position: {
    top: number;
    left: number;
    height: number;
    width: number;
  };
  orientation: TimelineOrientation;
  /** Time↔px mapping to read the gesture through; may be non-linear. */
  scale: TimelineScale;
  fromDateTime: DateTime;
  snapToMinutes: number;
};

/**
 * Converts a `Timeline.Selection` box to a `started_at`/`ended_at` pair through
 * a `TimelineScale`.
 *
 * Pixels are turned into time first, and snapping happens in the time domain —
 * minutes from `fromDateTime` rounded to `snapToMinutes`. That keeps the grid
 * even where the scale compresses time: a fixed px grid would snap to wildly
 * different intervals inside a collapsed stretch of the day.
 *
 * The duration is snapped after being corrected by however far the start moved,
 * so dragging across a whole number of slots yields that many slots.
 */
export const convertSelectionToDateTimeWithScale = ({
  position,
  orientation,
  scale,
  fromDateTime,
  snapToMinutes,
}: ScaleSelectionParams) => {
  const { startPx, lengthPx } =
    orientation === 'horizontal'
      ? { startPx: position.left, lengthPx: position.width }
      : { startPx: position.top, lengthPx: position.height };

  const minutesFromStart = (dateTime: DateTime) => dateTime.diff(fromDateTime, 'minutes').minutes;
  const snap = (minutes: number) => Math.round(minutes / snapToMinutes) * snapToMinutes;

  const startMinutesRaw = minutesFromStart(scale.toDateTime(startPx));
  const endMinutesRaw = minutesFromStart(scale.toDateTime(startPx + lengthPx));

  const startMinutes = snap(startMinutesRaw);
  const lengthAdjustment = startMinutes - startMinutesRaw;
  const durationMinutes = snap(endMinutesRaw - startMinutesRaw - lengthAdjustment);

  const started_at = fromDateTime.plus({ minutes: startMinutes });
  const ended_at = started_at.plus({ minutes: durationMinutes });

  return { started_at, ended_at };
};

/**
 * The default `TimelineScale`: every minute is `pxPerMinute` pixels, measured
 * from `fromDateTime`. `totalPx` covers `totalMinutes` (defaults to one day).
 */
export const createLinearTimelineScale = (
  fromDateTime: DateTime,
  pxPerMinute: number,
  totalMinutes: number = 24 * 60
): TimelineScale => ({
  toPx: dateTime => dateTime.diff(fromDateTime, 'minutes').minutes * pxPerMinute,
  toDateTime: px => fromDateTime.plus({ minutes: px / pxPerMinute }),
  totalPx: totalMinutes * pxPerMinute,
});
